import { storableError } from '../../util/errors';
import { denormalisedResponseEntities } from '../../util/data';
import { getTokensFromCookies } from '../../util/cookieUtils';
import { listThreadMessages, createThreadMessage, getProviderSalesCount, emailHandler, sendMessageWhatsapp } from '../../util/api';
import { fetchCurrentUserMessages } from '../../ducks/user.duck';

// ================ Action types ================ //

export const RESET_MESSAGE_PAGE = 'app/MessagePage/RESET_MESSAGE_PAGE';
export const FETCH_MESSAGES_REQUEST = 'app/MessagePage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/MessagePage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/MessagePage/FETCH_MESSAGES_ERROR';
export const FETCH_LISTING_SUCCESS = 'app/MessagePage/FETCH_LISTING_SUCCESS';
export const FETCH_USER_SUCCESS = 'app/MessagePage/FETCH_USER_SUCCESS';
export const FETCH_SALES_COUNT_REQUEST = 'app/MessagePage/FETCH_SALES_COUNT_REQUEST';
export const FETCH_SALES_COUNT_SUCCESS = 'app/MessagePage/FETCH_SALES_COUNT_SUCCESS';
export const FETCH_SALES_COUNT_ERROR = 'app/MessagePage/FETCH_SALES_COUNT_ERROR';
export const SEND_MESSAGE_REQUEST = 'app/MessagePage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/MessagePage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/MessagePage/SEND_MESSAGE_ERROR';
export const LOAD_MORE_MESSAGES_REQUEST = 'app/MessagePage/LOAD_MORE_MESSAGES_REQUEST';
export const LOAD_MORE_MESSAGES_SUCCESS = 'app/MessagePage/LOAD_MORE_MESSAGES_SUCCESS';
export const LOAD_MORE_MESSAGES_ERROR = 'app/MessagePage/LOAD_MORE_MESSAGES_ERROR';

// ================ Reducer ================ //

const initialState = {
  messages: [],
  listing: null,
  otherUser: null,
  fetchMessagesInProgress: false,
  sendMessageInProgress: false,
  currentPage: 1,
  totalPages: 1,
  salesCount: 0,
  params: null,
};

export default function messagePageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case RESET_MESSAGE_PAGE:
      return initialState;
    case FETCH_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true, params: payload.params };
    case FETCH_MESSAGES_SUCCESS:
      return { 
        ...state, 
        fetchMessagesInProgress: false, 
        messages: payload.messages,
        totalPages: payload.totalPages
      };
    case FETCH_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false };
    case FETCH_LISTING_SUCCESS:
      return { ...state, listing: payload.listing };
    case FETCH_USER_SUCCESS:
      return { ...state, otherUser: payload.user };
    case FETCH_SALES_COUNT_SUCCESS:
      return { ...state, salesCount: payload.salesCount };
    case SEND_MESSAGE_REQUEST:
      return { ...state, sendMessageInProgress: true };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false, messages: [...state.messages, payload.message] };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false };
    case LOAD_MORE_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true };
    case LOAD_MORE_MESSAGES_SUCCESS:
      return { 
        ...state, 
        fetchMessagesInProgress: false, 
        messages: [...payload.messages, ...state.messages],
        currentPage: payload.currentPage
      };
    case LOAD_MORE_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false };
    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchMessagesSuccess = (messages, totalPages ) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, totalPages },
});

const fetchListingSuccess = listing => ({
  type: FETCH_LISTING_SUCCESS,
  payload: { listing },
});

const fetchUserSuccess = user => ({
  type: FETCH_USER_SUCCESS,
  payload: { user },
});

const fetchSalesCountSuccess = salesCount => ({
  type: FETCH_SALES_COUNT_SUCCESS,
  payload: { salesCount },
});

const sendMessageSuccess = message => ({
  type: SEND_MESSAGE_SUCCESS,
  payload: { message },
});

export const resetMessagePage = () => ({
  type: RESET_MESSAGE_PAGE,
});

// ================ Thunks ================ //

export const loadData = params => async (dispatch, getState, sdk) => {
  dispatch(resetMessagePage());
  dispatch({ type: FETCH_MESSAGES_REQUEST, payload: { params } });

  try {
    const { accessToken, refreshToken } = getTokensFromCookies();

    const response = await listThreadMessages(params.threadId, {}, { 
      'legacy-auth-token': accessToken,
      'refresh-token': refreshToken
    });

    const messages = response.entries;
    const totalPages = response.total_pages;
    const thread = response.thread;

    dispatch(fetchMessagesSuccess(messages, totalPages));
    dispatch(fetchCurrentUserMessages());

    const listingId = thread.listing_id;
    const otherUserId = thread.other_user_id;

    // Fetch listing with author relationship
    return sdk.listings
      .show({ id: listingId, 
        include: ['images', 'author'],
        'fields.image': [
          // Listing page
          'variants.landscape-crop',
          'variants.landscape-crop2x',
          'variants.landscape-crop4x',
          'variants.landscape-crop6x',
    
          // Social media
          'variants.facebook',
          'variants.twitter',
    
          // Image carousel
          'variants.scaled-small',
          'variants.scaled-medium',
          'variants.scaled-large',
          'variants.scaled-xlarge',
    
          // Avatars
          'variants.square-small',
          'variants.square-small2x',
        ],
      })
      .then(response => {
        const listing = denormalisedResponseEntities(response);
        dispatch(fetchListingSuccess(listing[0]));
      })
      .catch(e => {
        console.error(e);
        throw storableError(e);
      })
      .then(() => {
        // Fetch other user
        return sdk.users
          .show({ id: otherUserId, include: ['profileImage'] })
          .then(async response => {
            const user = denormalisedResponseEntities(response);
            dispatch(fetchUserSuccess(user[0]));

            // Fetch sales count using the API utility
            try {
              const salesData = await getProviderSalesCount({ providerId: otherUserId });
              dispatch(fetchSalesCountSuccess(salesData.sellerSales));
            } catch (error) {
              console.error('Error fetching sales count:', error);
            }
          })
          .catch(e => {
            console.error(e);
            throw storableError(e);
          });
      });

  } catch (error) {
    console.error('Failed to fetch threads:', error);
    dispatch({ type: FETCH_MESSAGES_ERROR });
    throw storableError(error);
  }
};

export const onSendMessage = (threadId, messageContent) => async (dispatch, getState) => {
  dispatch({ type: SEND_MESSAGE_REQUEST });

  try {
    const { accessToken, refreshToken } = getTokensFromCookies();

    const response = await createThreadMessage(threadId, { message: messageContent }, { 
      'legacy-auth-token': accessToken,
      'refresh-token': refreshToken
    });

    const newMessage = response;
    if (!newMessage || !newMessage.id || !newMessage.message || !newMessage.sender_id || !newMessage.timestamp) {
      throw new Error('Invalid message structure');
    }

    dispatch(sendMessageSuccess(newMessage));
    
    // Send email notification
    try {
      // Get the current user from the state
      const state = getState();
      const currentUser = state.user.currentUser;
      
      // Get the other user from the state
      const otherUser = state.MessagePage.otherUser;
      
      // Prepare email data
      const emailData = {
        sender: {
          displayName: currentUser.attributes.profile.displayName
        },
        content: messageContent,
        threadId: threadId
      };

      console.log({emailData, otherUser})
      
      // Send email notification using the emailHandler function
      await emailHandler({
        emailType: 'NewMessage',
        emailReceiver: otherUser.attributes.email,
        emailData: emailData,
        requireUserQuery: true,
        requireUserQueryId: otherUser.id.uuid
      });
      
      // Send WhatsApp notification
      const sender = currentUser.id.uuid;
      const receiver = otherUser.id.uuid;
      const listing = state.MessagePage.listing;
      const type = 'message';
      
      sendMessageWhatsapp({ sender, receiver, currentListing: listing, type });
    } catch (emailError) {
      console.error('Error sending email notification:', emailError);
      // Don't throw the error to avoid affecting the message sending flow
    }
  } catch (error) {
    console.error('Error sending message:', error);
    dispatch({ type: SEND_MESSAGE_ERROR, error });
  }
};

export const loadMoreMessages = (threadId) => async (dispatch, getState) => {
  const { currentPage, totalPages } = getState().MessagePage;

  // Check if there are more pages to load
  if (currentPage >= totalPages) {
    return; // Exit early if no more pages
  }

  dispatch({ type: LOAD_MORE_MESSAGES_REQUEST });

  try {
    const { accessToken, refreshToken } = getTokensFromCookies();
    const nextPage = currentPage + 1;

    const response = await listThreadMessages(threadId, { page: nextPage }, { 
      'legacy-auth-token': accessToken,
      'refresh-token': refreshToken
    });

    const messagesFromResponse = response.entries;

    dispatch({ type: LOAD_MORE_MESSAGES_SUCCESS, payload: { messages: messagesFromResponse, currentPage: nextPage } });
  } catch (error) {
    console.error('Failed to load more messages:', error);
    dispatch({ type: LOAD_MORE_MESSAGES_ERROR });
  }
};

